import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { Badge } from '@/components/ui/Badge';
import { mockNfaLfaService } from '@/services/mock/MockNfaLfaService';
import { NoteForApproval } from '@/types/domain';
import { formatDate } from '@/lib/utils';
import {
  FileCheck,
  Clock,
  CheckCircle2,
  Eye,
  Pencil,
  Trash2,
  TrendingUp,
  AlertTriangle,
  Loader2,
  X,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { NfaDetailsPage } from './NfaDetailsPage';
import { unifiedLifecycleCoordinator } from '@/services/mock/UnifiedLifecycleCoordinator';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatInrCompact = (amount: number) => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)} Cr`;
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)} L`;
  }
  return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

export const NfaListPage: React.FC = () => {
  const navigate = useNavigate();
  const [nfas, setNfas] = useState<NoteForApproval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');

  // Deletion State
  const [nfaToDelete, setNfaToDelete] = useState<NoteForApproval | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedNfaForDrawer, setSelectedNfaForDrawer] = useState<string | null>(null);

  // Quick Sign Off State
  const [quickApproveTarget, setQuickApproveTarget] = useState<NoteForApproval | null>(null);
  const [isApprovingQuick, setIsApprovingQuick] = useState(false);
  const [quickComments, setQuickComments] = useState('');

  const loadNfas = async () => {
    try {
      setIsLoading(true);
      const res = await mockNfaLfaService.getNfas({
        status: selectedStatus === 'ALL' ? undefined : selectedStatus,
      });
      setNfas(res.nfas);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNfas();
  }, [selectedStatus]);

  // Lock body scroll when delete modal is open
  useEffect(() => {
    if (!nfaToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [nfaToDelete]);

  const handleConfirmDelete = async () => {
    if (!nfaToDelete) return;
    const targetId = nfaToDelete.id;
    try {
      setIsDeleting(true);
      // Immediately remove from local state
      setNfas((prev) => prev.filter((item) => item.id !== targetId));
      await mockNfaLfaService.deleteNfa(targetId);
      setNfaToDelete(null);
    } catch (err) {
      console.error('Failed to delete NFA:', err);
      loadNfas();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExecuteQuickApprove = async () => {
    if (!quickApproveTarget) return;
    try {
      setIsApprovingQuick(true);
      await mockNfaLfaService.approveStage({
        nfaId: quickApproveTarget.id,
        stageId: 'stg-2',
        comments: quickComments || 'Executive Committee Signoff Authorized',
        version: quickApproveTarget.version,
      });
      await unifiedLifecycleCoordinator.onNfaApproved(
        quickApproveTarget.id,
        quickComments || 'Executive Committee Signoff Authorized'
      );
      setQuickApproveTarget(null);
      setQuickComments('');
      await loadNfas();
    } catch (err: any) {
      console.error('Failed to approve NFA:', err);
      await loadNfas();
    } finally {
      setIsApprovingQuick(false);
    }
  };

  // Aggregate KPI metrics
  const totalCount = nfas.length;
  const underApprovalCount = nfas.filter(
    (n) => n.status === 'UnderApproval' || n.status === 'ClarificationRequested'
  ).length;
  const approvedCount = nfas.filter((n) => n.status === 'Approved').length;
  const totalValue = nfas.reduce((sum, n) => sum + (n.estimatedCost || 0), 0);
  const totalSavings = nfas.reduce((sum, n) => sum + (n.savingsEstimated || 0), 0);

  const columns: ColumnDef<NoteForApproval>[] = [
    {
      header: 'NFA Number',
      accessorKey: 'nfaNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.NFA.DETAILS(row.id))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.nfaNumber}
          </span>
          <span className="text-xs text-foreground-muted font-mono block whitespace-nowrap">
            {formatDate(row.createdAt)} • REQ: {row.requestNumber}
          </span>
        </div>
      ),
    },
    {
      header: 'Title & Purpose',
      accessorKey: 'title',
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[190px] xl:max-w-[220px] font-poppins">
          <span
            className="font-bold text-foreground text-sm truncate block"
            title={row.title}
          >
            {row.title}
          </span>
          <span
            className="text-xs text-foreground-secondary truncate block"
            title={row.subject || row.commercialJustification}
          >
            {row.subject || row.commercialJustification}
          </span>
        </div>
      ),
    },
    {
      header: 'Department',
      accessorKey: 'departmentName',
      cell: (row) => (
        <div className="max-w-[130px] xl:max-w-[160px] truncate font-poppins" title={row.departmentName}>
          <span className="text-sm font-semibold text-foreground truncate block">
            {row.departmentName}
          </span>
          <span className="text-xs text-foreground-muted font-mono block">
            {row.costCenterCode}
          </span>
        </div>
      ),
    },
    {
      header: 'Classification',
      cell: (row) => (
        <Badge variant="outline" className="text-xs font-bold whitespace-nowrap font-poppins px-2 py-0.5">
          {row.capexOpex}
        </Badge>
      ),
    },
    {
      header: 'Proposed Value',
      accessorKey: 'estimatedCost',
      cell: (row) => (
        <div className="font-poppins whitespace-nowrap">
          <span className="font-bold text-foreground font-mono text-sm block">
            {formatInr(row.estimatedCost)}
          </span>
          <span className="font-semibold text-emerald-600 dark:text-emerald-400 font-mono text-[11px] block">
            +{formatInr(row.savingsEstimated)} saved
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          <StatusBadge
            status={row.status}
            category="NFA"
            className="w-32 py-1 justify-center text-center font-semibold text-xs rounded-full shadow-2xs font-poppins whitespace-nowrap"
          />
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => {
        const isActionable = row.status === 'UnderApproval' || row.status === 'ClarificationRequested';
        return (
          <div
            className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[145px]"
            onClick={(e) => e.stopPropagation()}
          >
            {isActionable ? (
              <Button
                size="sm"
                onClick={() => setQuickApproveTarget(row)}
                className="h-8 px-2.5 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 shadow-2xs cursor-pointer font-poppins transition-all hover:scale-105 active:scale-95"
                title="Authorize Executive Signoff (Release PO & LFA)"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span>Sign Off</span>
              </Button>
            ) : (
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-lg">
                ✓ Authorized
              </span>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedNfaForDrawer(row.id)}
              title="View NFA Dossier (Slide-over)"
              className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
            >
              <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.NFA.EDIT(row.id))}
              title="Edit NFA"
              className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNfaToDelete(row)}
              title="Delete NFA"
              className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <FileCheck className="h-7 w-7 text-primary" /> Notes For Approval (NFA)
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Commercial authorization dossiers capturing vendor negotiations, cost-center budget clearance, and multi-tier delegation of authority (DOA) governance.
          </p>
        </div>
      </div>

      {/* KPI Metric Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3.5 font-poppins">
        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Total NFAs</span>
            <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
              <FileCheck className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-foreground block">{totalCount}</span>
          <span className="text-[10px] text-foreground-muted">Active audit records</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Under Approval</span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-amber-600 block">{underApprovalCount}</span>
          <span className="text-[10px] text-foreground-muted">Pending authorization</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Authorized</span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-emerald-600 block">{approvedCount}</span>
          <span className="text-[10px] text-foreground-muted">Released for PO</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Committed Spend</span>
            <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 font-mono font-bold text-xs">
              ₹
            </div>
          </div>
          <span className="text-xl font-extrabold text-foreground block font-mono">
            {formatInrCompact(totalValue)}
          </span>
          <span className="text-[10px] text-foreground-muted">Total proposed value</span>
        </Card>

        <Card className="rounded-2xl border border-border p-4 space-y-1 bg-card shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-foreground-muted">Negotiated Savings</span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
              <TrendingUp className="h-4 w-4" />
            </div>
          </div>
          <span className="text-xl font-extrabold text-emerald-600 block font-mono">
            +{formatInrCompact(totalSavings)}
          </span>
          <span className="text-[10px] text-foreground-muted">Verified cost avoidance</span>
        </Card>
      </div>

      {/* Main Ledger Card */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            {/* Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All NFAs' },
                { key: 'UnderApproval', label: 'Under Approval' },
                { key: 'Approved', label: 'Approved' },
                { key: 'Rejected', label: 'Rejected' },
                { key: 'SentBack', label: 'Sent Back' },
              ].map((tab) => {
                const isActive = selectedStatus === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setSelectedStatus(tab.key)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-hidden">
          <DataTable
            data={nfas}
            columns={columns}
            isLoading={isLoading}
            searchPlaceholder="Search NFA #, Title, REQ #, Department..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-hidden"
            emptyTitle="No Notes for Approval Found"
            emptyDescription="There are no commercial approval records matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.NFA.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal (Mounted via createPortal to document.body) */}
      {nfaToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setNfaToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Delete Note for Approval?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setNfaToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Dossier Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    NFA Reference
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {nfaToDelete.nfaNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Subject
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {nfaToDelete.title}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Proposed Value
                  </span>
                  <span className="font-bold text-foreground font-mono">
                    {formatInr(nfaToDelete.estimatedCost)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to delete this Note for Approval? This action removes the commercial approval record from the ledger and cannot be undone.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setNfaToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Delete NFA</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Quick Sign Off Modal (Mounted via createPortal to document.body) */}
      {quickApproveTarget &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isApprovingQuick && setQuickApproveTarget(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl bg-card border border-border p-6 shadow-2xl space-y-4 animate-in zoom-in-95 duration-200 font-poppins"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 border-b border-border/60 pb-3">
                <div className="p-2.5 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base text-foreground">
                    Authorize Commercial Signoff
                  </h3>
                  <p className="text-xs text-foreground-secondary font-mono">
                    {quickApproveTarget.nfaNumber} • Ref: {quickApproveTarget.requestNumber}
                  </p>
                </div>
              </div>

              <div className="space-y-2 text-xs text-foreground-secondary">
                <p>
                  You are authorizing commercial clearance for <strong className="text-foreground">{quickApproveTarget.title}</strong>.
                </p>
                <div className="p-3 rounded-xl bg-muted/40 border border-border space-y-1.5">
                  <div className="flex justify-between">
                    <span>Proposed Value:</span>
                    <strong className="text-foreground font-mono text-sm">{formatInr(quickApproveTarget.estimatedCost)}</strong>
                  </div>
                  <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
                    <span>Negotiated Savings:</span>
                    <strong className="font-mono">+{formatInr(quickApproveTarget.savingsEstimated)}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>Cost Center:</span>
                    <strong className="text-foreground font-mono">{quickApproveTarget.costCenterCode}</strong>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">Executive Remarks</label>
                <input
                  type="text"
                  value={quickComments}
                  onChange={(e) => setQuickComments(e.target.value)}
                  placeholder="e.g., Commercial terms approved per steering committee"
                  className="w-full px-3 py-2 text-xs rounded-xl border border-border bg-background focus:outline-hidden focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isApprovingQuick}
                  onClick={() => setQuickApproveTarget(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Cancel
                </Button>
                <button
                  type="button"
                  disabled={isApprovingQuick}
                  onClick={handleExecuteQuickApprove}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-emerald-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isApprovingQuick ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Authorizing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>Authorize & Release PO</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for NFA Dossier */}
      <SlideOverDrawer
        isOpen={Boolean(selectedNfaForDrawer)}
        onClose={() => setSelectedNfaForDrawer(null)}
        title="Note for Approval (NFA) Dossier"
        subtitle="Commercial approval, multi-tier DOA workflow, budget clearance, and audit trace"
        fullPageUrl={selectedNfaForDrawer ? ROUTES.NFA.DETAILS(selectedNfaForDrawer) : undefined}
      >
        {selectedNfaForDrawer && (
          <NfaDetailsPage
            recordId={selectedNfaForDrawer}
            isDrawer={true}
            onClose={() => setSelectedNfaForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
